import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"
import { marked } from "marked"

// marked 설정 - 커스텀 렌더러
marked.use({
  renderer: {
    // strikethrough 비활성화 (~ 문자가 취소선으로 해석되는 것 방지)
    del(token) {
      return token.text
    },
    // --- 를 구분선으로 표시
    hr() {
      return '<hr class="my-4 border-t border-gray-300 dark:border-gray-600">'
    }
  }
})
marked.setOptions({
  breaks: true,  // 줄바꿈을 <br>로 변환
  gfm: true       // GitHub Flavored Markdown (테이블 지원)
})

export default class extends Controller {
  static values = {
    sessionId: String,
    writerId: Number,
    writerName: String,
    writerImage: String,
    spaceUrl: String,
    resetSessionUrl: String,
    // 서버(embed.html.erb)가 내려주는 UI 문구 사전. 없으면 아래 한국어 fallback 을 쓴다.
    i18n: { type: Object, default: {} }
  }

  // 번역 문구 조회 (없으면 기존 한국어 fallback)
  t(key, fallback) {
    const value = this.i18nValue?.[key]
    return typeof value === "string" && value.length > 0 ? value : fallback
  }

  static targets = [
    "messages",
    "emptyState",
    "form",
    "input",
    "submitButton",
    "sendIcon",
    "loadingIcon",
    "scrollContainer",
    "aiResponseMessage",
    "streamingContent",
    "messageContainer",
    "resetSection"
  ]

  connect() {
    this.currentMessageId = null
    this.isStreaming = false
    this.streamingContent = ""  // 스트리밍 중 누적 콘텐츠

    // ActionCable 구독
    this.subscription = createConsumer().subscriptions.create(
      { channel: "AiChatChannel", session_id: this.sessionIdValue },
      {
        connected: () => console.log("AI Chat connected"),
        disconnected: () => console.log("AI Chat disconnected"),
        received: (data) => this.handleMessage(data)
      }
    )

    // 기존 메시지들의 마크다운 렌더링
    this.renderExistingMessages()

    // 스크롤을 맨 아래로
    this.scrollToBottom()
  }

  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe()
    }
  }

  renderExistingMessages() {
    this.aiResponseMessageTargets.forEach(el => {
      const rawContent = el.textContent
      if (rawContent) {
        el.innerHTML = this.formatContent(rawContent)
      }
      // Tailwind으로 페이드인
      el.classList.remove('opacity-0')
      el.classList.add('opacity-100', 'transition-opacity', 'duration-150')
    })
  }

  handleMessage(data) {
    switch(data.type) {
      case 'user_message':
        this.appendUserMessage(data)
        break
      case 'stream_start':
        this.startStreaming(data)
        break
      case 'chunk':
        this.appendChunk(data)
        break
      case 'stream_end':
        this.endStreaming(data)
        break
      case 'stream_error':
        this.handleStreamError(data)
        break
    }
  }

  appendUserMessage(data) {
    // 빈 상태 메시지 제거
    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.remove()
    }

    // HTML 직접 추가
    this.messagesTarget.insertAdjacentHTML('beforeend', data.html)
    this.scrollToBottom()
  }

  startStreaming(data) {
    this.isStreaming = true
    this.currentMessageId = data.message_id
    this.streamingContent = ""  // 스트리밍 콘텐츠 초기화

    // 빈 상태 메시지 제거
    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.remove()
    }

    // 대기 중 로딩 메시지 제거
    this.removePendingMessage()

    // Writer 아바타 HTML 생성 (ERB _message.html.erb와 동일)
    const writerAvatarHtml = this.writerImageValue
      ? `<img src="${this.writerImageValue}" alt="${this.writerNameValue}" class="w-6 h-6 rounded-full object-cover">`
      : `<div class="w-6 h-6 rounded-full bg-gradient-to-br from-gray-400 to-gray-600 flex items-center justify-center text-white text-xs font-bold">${this.writerNameValue?.charAt(0) || 'AI'}</div>`

    // AI 메시지 컨테이너 생성 (ERB _message.html.erb와 동일한 구조)
    const messageHtml = `
      <div id="message-${data.message_id}" class="flex justify-start" data-role="chat_response">
        <div class="max-w-[95%] order-1">
          <div class="flex items-start gap-2">
            <div class="flex-shrink-0">
              ${writerAvatarHtml}
            </div>
            <div class="flex-1 min-w-0" data-ai-chat-target="messageContainer">
              <div class="bg-white dark:bg-gray-800 rounded-xl rounded-tl-sm px-3 py-2 shadow-sm border border-gray-100 dark:border-gray-700">
                <div class="prose prose-sm dark:prose-invert max-w-none text-sm whitespace-normal" data-ai-chat-target="streamingContent">
                  <span class="inline-flex gap-1 py-1">
                    <span class="w-2 h-2 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.3s]"></span>
                    <span class="w-2 h-2 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.15s]"></span>
                    <span class="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `

    this.messagesTarget.insertAdjacentHTML('beforeend', messageHtml)
    this.scrollToBottom()
  }

  appendChunk(data) {
    if (!this.hasStreamingContentTarget) return

    // 콘텐츠 누적
    this.streamingContent += data.content

    // 콘텐츠가 있을 때만 렌더링 (빈 상태면 로딩 애니메이션 유지)
    if (this.streamingContent.trim()) {
      this.streamingContentTarget.innerHTML = this.formatContent(this.streamingContent)
    }

    this.scrollToBottom()
  }

  endStreaming(data) {
    this.isStreaming = false
    this.currentMessageId = null

    if (!this.hasStreamingContentTarget) return

    // 타이핑 인디케이터 제거 (첫 번째 자식 span)
    const typingIndicator = this.streamingContentTarget.querySelector('.inline-flex')
    if (typingIndicator) {
      typingIndicator.remove()
    }

    // 최종 콘텐츠로 업데이트 (줄바꿈 처리)
    this.streamingContentTarget.innerHTML = this.formatContent(data.content)

    // 참조 뉴스레터 링크 표시 (answered가 true일 때만)
    const isAnswered = data.answered !== false
    if (isAnswered && data.referenced_posts && data.referenced_posts.length > 0) {
      const referencedHtml = this.renderReferencedPosts(data.referenced_posts)
      this.streamingContentTarget.insertAdjacentHTML('afterend', referencedHtml)
    }

    // 스트리밍 완료 후 target 속성 변경 (CSS 유지를 위해 aiResponseMessage로)
    this.streamingContentTarget.dataset.aiChatTarget = 'aiResponseMessage'

    // 타임스탬프 추가
    if (this.hasMessageContainerTarget) {
      const actionsHtml = `
        <div class="mt-1.5 flex items-center gap-2 ml-1">
          <span class="text-xs text-gray-400 dark:text-gray-500">
            ${new Date().toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>
      `
      this.messageContainerTarget.insertAdjacentHTML('beforeend', actionsHtml)
      delete this.messageContainerTarget.dataset.aiChatTarget
    }

    this.setLoading(false)
    this.scrollToBottom()

    // 새 채팅 시작 섹션 표시
    this.showResetSection()
  }

  // 토스트 메시지 표시
  showToast(message, type = 'success') {
    const toast = document.createElement('div')
    const bgColor = type === 'error'
      ? 'bg-red-500'
      : 'bg-green-500'

    toast.className = `fixed bottom-4 right-4 ${bgColor} text-white px-4 py-2 rounded-lg shadow-lg z-50 transition-opacity duration-300`
    toast.textContent = message
    document.body.appendChild(toast)

    setTimeout(() => {
      toast.classList.add('opacity-0')
      setTimeout(() => toast.remove(), 300)
    }, 3000)
  }

  // 참조된 뉴스레터 포스트 링크 렌더링 (구독자용)
  // 데스크탑: ai_chat=true 포함하여 페이지 이동 (채팅 유지)
  // 모바일: 일반 페이지 이동 (채팅 닫힘)
  renderReferencedPosts(posts) {
    if (!posts || posts.length === 0) return ''

    const truncate = (str, maxLength) => {
      if (!str) return this.t('newsletter_fallback', '뉴스레터')
      return str.length > maxLength ? str.slice(0, maxLength) + '...' : str
    }

    const postLinks = posts.slice(0, 5).map(post => {
      const url = this.spaceUrlValue
        ? `/${this.spaceUrlValue}/posts/${post.ext_id}`
        : `#`
      const urlWithChat = this.spaceUrlValue
        ? `/${this.spaceUrlValue}/posts/${post.ext_id}?ai_chat=true`
        : `#`
      const membershipLabel = post.is_membership
        ? `<span class="text-green-600 dark:text-green-400">${this.escapeHtml(this.t('membership_badge', '[멤버십]'))}</span>`
        : ''
      return `
        <a href="${this.escapeHtml(url)}"
           data-post-url="${this.escapeHtml(url)}"
           data-post-url-with-chat="${this.escapeHtml(urlWithChat)}"
           onclick="return handlePostLinkClick(event, this)"
           class="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-gray-700 bg-gray-100 dark:bg-gray-700 dark:text-gray-300 dark:hover:text-gray-200 px-2 py-0.5 rounded">
          <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
          </svg>
          ${membershipLabel}
          ${this.escapeHtml(truncate((post.title || '').replace(/%name%/g, this.t('subscriber_name', '구독자')), 25))}
        </a>
      `
    }).join('')

    return `
      <div class="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
        <p class="text-xs text-gray-400 dark:text-gray-500 mb-1">${this.escapeHtml(this.t('referenced_posts_label', '참고한 뉴스레터:'))}</p>
        <div class="flex flex-wrap gap-1">
          ${postLinks}
        </div>
      </div>
    `
  }

  handleStreamError(data) {
    this.isStreaming = false
    this.currentMessageId = null

    // 대기 중 로딩 메시지 제거
    this.removePendingMessage()

    if (this.hasStreamingContentTarget) {
      this.streamingContentTarget.innerHTML = `<span class="text-red-500">${data.content}</span>`
      delete this.streamingContentTarget.dataset.aiChatTarget
    }

    if (this.hasMessageContainerTarget) {
      delete this.messageContainerTarget.dataset.aiChatTarget
    }

    this.setLoading(false)
  }

  sendMessage(event) {
    event.preventDefault()

    const content = this.inputTarget.value.trim()
    if (!content || this.isStreaming) return

    // UI 업데이트
    this.setLoading(true)
    this.inputTarget.value = ''
    this.autoResize()

    // 빈 상태 메시지 제거
    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.remove()
    }

    // 사용자 메시지 즉시 표시
    // 사용자 메시지 (ERB _message.html.erb와 동일한 구조)
    const userMessageHtml = `
      <div class="flex justify-end" data-role="user">
        <div class="max-w-[95%] order-2">
          <div class="flex items-start gap-2 justify-end">
            <div class="flex-1 text-right">
              <div class="inline-block bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white rounded-xl rounded-tr-sm px-3 py-2 text-left">
                <div class="text-sm whitespace-normal">${this.escapeHtml(content)}</div>
              </div>
              <div class="mt-0.5 text-xs text-gray-400 dark:text-gray-500 mr-1">
                ${new Date().toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
          </div>
        </div>
      </div>
    `
    this.messagesTarget.insertAdjacentHTML('beforeend', userMessageHtml)
    this.scrollToBottom()

    // AI 응답 대기 중 로딩 표시
    this.showPendingMessage()

    // 서버에 메시지 전송
    const url = this.formTarget.action || window.location.pathname + '/send_message'
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.content
      },
      body: new URLSearchParams({ content: content })
    }).then(response => {
      if (!response.ok) {
        this.removePendingMessage()
        this.setLoading(false)

        if (response.status === 403) {
          this.showErrorMessage(this.t('error_forbidden', 'AI 채팅 서비스가 현재 이용 불가능합니다. 운영자가 서비스를 비활성화했거나 접근 권한이 없습니다.'))
        } else if (response.status === 503) {
          this.showErrorMessage(this.t('error_preparing', 'AI 채팅이 준비 중입니다. 잠시 후 다시 시도해주세요.'))
        } else {
          this.showErrorMessage(this.t('error_send_failed', '메시지 전송에 실패했습니다. 잠시 후 다시 시도해주세요.'))
        }
      }
    }).catch(error => {
      console.error('Failed to send message:', error)
      this.removePendingMessage()
      this.setLoading(false)
      this.showErrorMessage(this.t('error_network', '네트워크 오류가 발생했습니다. 인터넷 연결을 확인해주세요.'))
    })
  }

  showErrorMessage(message) {
    // Writer 아바타 HTML 생성
    const writerAvatarHtml = this.writerImageValue
      ? `<img src="${this.writerImageValue}" alt="${this.writerNameValue}" class="w-6 h-6 rounded-full object-cover">`
      : `<div class="w-6 h-6 rounded-full bg-gradient-to-br from-gray-400 to-gray-600 flex items-center justify-center text-white text-xs font-bold">${this.writerNameValue?.charAt(0) || 'AI'}</div>`

    const errorMessageHtml = `
      <div class="flex justify-start" data-role="chat_response">
        <div class="max-w-[95%] order-1">
          <div class="flex items-start gap-2">
            <div class="flex-shrink-0">
              ${writerAvatarHtml}
            </div>
            <div class="flex-1 min-w-0">
              <div class="bg-red-50 dark:bg-red-900/20 rounded-xl rounded-tl-sm px-3 py-2 shadow-sm border border-red-200 dark:border-red-800">
                <div class="text-sm text-red-600 dark:text-red-400">
                  ${this.escapeHtml(message)}
                </div>
              </div>
              <div class="mt-0.5 text-xs text-gray-400 dark:text-gray-500 ml-1">
                ${new Date().toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
          </div>
        </div>
      </div>
    `

    this.messagesTarget.insertAdjacentHTML('beforeend', errorMessageHtml)
    this.scrollToBottom()
  }

  handleKeydown(event) {
    // Enter 키로 전송 (Shift+Enter는 줄바꿈)
    // IME 조합 중(한글 입력 등)에는 전송하지 않음
    if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
      event.preventDefault()
      this.sendMessage(event)
    }
  }

  autoResize() {
    const textarea = this.inputTarget
    textarea.style.height = 'auto'
    textarea.style.height = Math.min(textarea.scrollHeight, 150) + 'px'
  }

  setLoading(loading) {
    this.submitButtonTarget.disabled = loading

    if (loading) {
      this.sendIconTarget.classList.add('hidden')
      this.loadingIconTarget.classList.remove('hidden')
    } else {
      this.sendIconTarget.classList.remove('hidden')
      this.loadingIconTarget.classList.add('hidden')
    }
  }

  scrollToBottom() {
    requestAnimationFrame(() => {
      if (this.hasScrollContainerTarget) {
        this.scrollContainerTarget.scrollTop = this.scrollContainerTarget.scrollHeight
      }
    })
  }

  formatContent(content) {
    if (!content) return ''

    // marked를 사용하여 마크다운을 HTML로 변환
    // trim()으로 trailing whitespace 제거 (whitespace-normal과 함께 사용 시 빈 줄 방지)
    let html = marked.parse(content).trim()

    // 테이블을 스크롤 가능한 wrapper로 감싸기 (테이블만 가로 스크롤)
    html = html.replace(/<table>/g, '<div class="table-scroll-wrapper"><table>')
    html = html.replace(/<\/table>/g, '</table></div>')

    return html
  }

  showPendingMessage() {
    // Writer 아바타 HTML 생성 (ERB _message.html.erb와 동일)
    const writerAvatarHtml = this.writerImageValue
      ? `<img src="${this.writerImageValue}" alt="${this.writerNameValue}" class="w-6 h-6 rounded-full object-cover">`
      : `<div class="w-6 h-6 rounded-full bg-gradient-to-br from-gray-400 to-gray-600 flex items-center justify-center text-white text-xs font-bold">${this.writerNameValue?.charAt(0) || 'AI'}</div>`

    const pendingMessageHtml = `
      <div id="pending-ai-message" class="flex justify-start" data-role="chat_response">
        <div class="max-w-[95%] order-1">
          <div class="flex items-start gap-2">
            <div class="flex-shrink-0">
              ${writerAvatarHtml}
            </div>
            <div class="flex-1 min-w-0">
              <div class="bg-white dark:bg-gray-800 rounded-xl rounded-tl-sm px-3 py-2 shadow-sm border border-gray-100 dark:border-gray-700">
                <div class="prose prose-sm dark:prose-invert max-w-none text-sm whitespace-normal">
                  <span class="inline-flex gap-1 py-1">
                    <span class="w-2 h-2 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.3s]"></span>
                    <span class="w-2 h-2 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.15s]"></span>
                    <span class="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `

    this.messagesTarget.insertAdjacentHTML('beforeend', pendingMessageHtml)
    this.scrollToBottom()
  }

  removePendingMessage() {
    const pendingMessage = document.getElementById('pending-ai-message')
    if (pendingMessage) {
      pendingMessage.remove()
    }
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  // 채팅 세션 초기화 (새 채팅 시작)
  async resetSession() {
    if (!this.resetSessionUrlValue) {
      console.warn('reset_session URL이 설정되지 않았습니다.')
      return
    }

    // 확인 대화상자
    if (!confirm(this.t('confirm_reset', '현재 대화를 종료하고 새 채팅을 시작하시겠습니까?'))) {
      return
    }

    try {
      const response = await fetch(this.resetSessionUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.content,
          'Accept': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error('세션 초기화 실패')
      }

      const data = await response.json()

      if (data.success) {
        // turbo frame의 src를 새 embed URL로 변경하여 리로드
        const frameId = this.isSubscriber ? 'subscriber-floating-ai-chat' : 'floating-ai-chat'
        const frame = document.getElementById(frameId)
        if (frame && data.embed_url) {
          frame.src = data.embed_url
        } else {
          // fallback: 페이지 새로고침
          window.location.reload()
        }
      }
    } catch (error) {
      console.error('Reset session error:', error)
      this.showToast(this.t('reset_failed', '채팅 초기화에 실패했습니다.'), 'error')
    }
  }

  // 새 채팅 시작 섹션 표시 (첫 메시지 답변 후)
  showResetSection() {
    if (this.hasResetSectionTarget) {
      this.resetSectionTarget.classList.remove('hidden')
    }
  }

}
